import { Typography, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import React from 'react'

// Editable SMS text limit, matching the follow-up SMS editor (FollowUpDialog enforces 95 chars via
// Yup + counter). Kept short so the message plus the auto-appended estimate link stays near one
// segment. Soft cap: we let the text exceed it, surface an error, and block the send (see the
// dialog's ValidationSchema) rather than hard-truncating a longer default (the subject).
export const SMS_MAX_LENGTH = 95

const useErrorMessageStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    // Top-aligned, not centred: a message long enough to wrap (the SMS length error) keeps its icon on
    // the first line and hanging-indents the rest of the text past it.
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    color: theme.palette.error.main,
    // Never let the message squeeze the field it belongs to when it sits inline beside it.
    flexShrink: 0
  },
  icon: {
    fontSize: '1rem',
    // Optically centre the icon on the first line of text rather than on its cap height.
    marginTop: '0.125rem',
    flexShrink: 0
  }
}))

/** Inline field error styled consistently across the Compose form sections. */
export function ErrorMessage({ children }: { children: React.ReactNode }) {
  const classes = useErrorMessageStyles()

  return (
    <div className={classes.root}>
      <ErrorOutlineIcon className={classes.icon} />
      <Typography variant="subtitle2" color="error">
        {children}
      </Typography>
    </div>
  )
}

/**
 * True when editor HTML has no visible content. TipTap serializes an empty document as `<p></p>`
 * (often with a trailing `<br>`), so a plain Yup `.required()` on the body passes even when the user
 * has cleared it — this strips markup/whitespace so an empty body fails validation like the subject.
 */
export function isEmptyEditorHtml(html?: string) {
  if (!html) return true
  // An image-only body is real content even though it has no text.
  if (/<img\b/i.test(html)) return false
  return !html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/[\u200B\uFEFF]/g, '')
    .trim()
}

/** AddressBookSelect stores each option's `label` as the value, so value === label === the address. */
export function mapAddressesToOptions(to: string[] = []) {
  return to.map((address) => ({
    value: address,
    label: address
  }))
}

/**
 * True for an address-book group-wrapper option (e.g. `{label: 'Personal Address Book', options:
 * [...]}`) rather than a real address option. A group wrapper's `label` is a string too, so a
 * plain string check doesn't catch it — shared so both send-quote dialogs filter identically.
 */
export function isAddressBookGroupOption(option: { options?: unknown; [key: string]: unknown }): boolean {
  return option?.options !== undefined
}

/**
 * Inverse of mapAddressesToOptions: extracts each option's label as the recipient address.
 * Drops any option whose label isn't a string — a malformed option (e.g. a DropdownSelect group
 * wrapper) previously wrote a non-string into the `to` field, which crashed isEmailValid() at
 * send time (ENG-1645). Also drops any address-book group wrapper (see isAddressBookGroupOption):
 * without that, its own label is written into `to` as a fake address, which bounces and shows a
 * false "Unable to Send" error even though the real recipients succeed.
 */
export function mapOptionsToAddresses(
  options: Array<{ label?: unknown; options?: unknown; [key: string]: unknown }> = []
): string[] {
  return options
    .filter(
      (option): option is { label: string } => typeof option?.label === 'string' && !isAddressBookGroupOption(option)
    )
    .map((option) => option.label)
}
